import { apiDomain } from '@/ApiPaths';
import { ROBOTOP_UNITY_SESSION_MSG, ROBOTOP_POPUP_LOGIN_MSG } from '@/services/Authentication';

export { ROBOTOP_UNITY_SESSION_MSG, ROBOTOP_POPUP_LOGIN_MSG };

export type UnityHandoffTokenMap = {
  'st-access-token': string;
  'front-token'?: string;
};

export type UnitySessionPostMessage = {
  type: typeof ROBOTOP_UNITY_SESSION_MSG;
  accessToken: string;
  frontToken?: string;
  tokens?: UnityHandoffTokenMap;
};

/** Resolve the origin of the Unity iframe URL (for postMessage targetOrigin). */
export function getUnityIframeOrigin(iframeUrl: string, fallback = apiDomain): string {
  try {
    return new URL(iframeUrl, typeof window !== 'undefined' ? window.location.origin : fallback)
      .origin;
  } catch {
    return fallback;
  }
}

/** Build the payload Unity index.html / HostSessionBridge expects (access + front only). */
export function buildUnitySessionMessage(
  tokens: UnityHandoffTokenMap | Record<string, string>
): UnitySessionPostMessage | null {
  const accessToken = tokens['st-access-token'];
  if (!accessToken) return null;
  return {
    type: ROBOTOP_UNITY_SESSION_MSG,
    accessToken,
    frontToken: tokens['front-token'],
    tokens: {
      'st-access-token': accessToken,
      'front-token': tokens['front-token'],
    },
  };
}

/**
 * Post SuperTokens header tokens into the Unity WebGL iframe.
 * Prefer a concrete targetOrigin (never use "*" for tokens).
 */
export function postUnitySessionToIframe(
  iframe: HTMLIFrameElement | null | undefined,
  iframeUrl: string,
  tokens: UnityHandoffTokenMap | Record<string, string>
): boolean {
  const win = iframe?.contentWindow;
  if (!win) return false;
  const payload = buildUnitySessionMessage(tokens);
  if (!payload) return false;
  win.postMessage(payload, getUnityIframeOrigin(iframeUrl));
  return true;
}

export function isUnitySessionReadyMessage(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: string }).type === 'robotop-unity-session-ready'
  );
}

export function isRobotopPopupLoginMessage(data: unknown): data is {
  type: typeof ROBOTOP_POPUP_LOGIN_MSG;
  status?: string;
  userId?: string;
  tokens?: Record<string, string>;
  error?: string;
} {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: string }).type === ROBOTOP_POPUP_LOGIN_MSG
  );
}

/** Open centered Login-with-Robotop popup; returns the Window or null if blocked. */
export function openRobotopLoginPopup(openerOrigin: string = window.location.origin): Window | null {
  const loginUrl = new URL('/login', window.location.origin);
  loginUrl.searchParams.set('popup', '1');
  loginUrl.searchParams.set('opener_origin', openerOrigin);

  const width = 480;
  const height = 720;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  return window.open(
    loginUrl.toString(),
    'robotop_login',
    `popup=yes,width=${width},height=${height},left=${left},top=${top}`
  );
}
